import { useEffect } from "react";
import { useTexts } from "../texts";
import { Notice, PageHead } from "../ui/kit";
import { ListFooter, useServerList } from "../ui/list";
import { PageState, ServiceState } from "../ui/pieces";
import { attemptsText, duration, executionTone, money, number, operationName, plural, when } from "./ai";
import { useTitle } from "./parts";
import { sharedQuery, UsageFilters, UsageTabs } from "./UsagePage";
import { Counter, DataTable, NoResults, RowsSkeleton, useFilters } from "./widgets";
import styles from "./ai.module.css";

type ExecutionRow = {
  executionId: number;
  organizationId: number | null;
  operationType: string;
  status: string;
  attemptCount: number;
  totalInputTokens: number | null;
  totalOutputTokens: number | null;
  totalCost: number | null;
  currency: string | null;
  latencyMilliseconds: number | null;
  startedAt: string;
  organizationName: string | null;
};

/** Dónde volver desde una ejecución: la lista con su filtro (se recuerda en la pestaña, nunca datos de la persona). */
export const executionsBackKey = "npa-ai-executions";

/**
 * IA › Uso y costos › Ejecuciones (§5.9 `ia-uso-ejec`): el mismo filtro que el Resumen, paginado en el servidor con el
 * total real (B2). Clic en la fila abre la ejecución.
 */
export function ExecutionsPage() {
  const { L, locale } = useTexts();
  const { params, set } = useFilters();
  useTitle(`${L("ADM_AI_USAGE")} · ${L("ADM_AI_EXECUTIONS")}`);
  const shared = sharedQuery(params);
  const list = useServerList<ExecutionRow>(`/api/admin/ai/usage/executions${shared ? `?${shared}` : ""}`);
  const page = params.get("page");
  useEffect(() => {
    try {
      sessionStorage.setItem(executionsBackKey, `${shared}${page ? `${shared ? "&" : ""}page=${page}` : ""}`);
    } catch {
      // Sin almacenamiento, ← vuelve a la lista sin filtro.
    }
  }, [shared, page]);
  const head = (
    <>
      <PageHead title={L("ADM_AI_USAGE")} sub={L("ADM_AI_USAGE_SUB")} />
      <UsageTabs current="executions" />
    </>
  );
  if (list.state === "forbidden")
    return <PageState head={head} title={L("ADM_FORBIDDEN")} line={L("ADM_FORBIDDEN_LINE")} />;

  const organization = (row: ExecutionRow) =>
    row.organizationId == null ? L("ADM_AI_PLATFORM") : (row.organizationName ?? L("ADM_AI_ORG_OUT_OF_SCOPE"));
  const tokens = (row: ExecutionRow) =>
    row.totalInputTokens == null && row.totalOutputTokens == null
      ? "—"
      : `${number(locale, row.totalInputTokens)} / ${number(locale, row.totalOutputTokens)}`;
  const filtered = !!(params.get("type") || params.get("organizationId"));

  return (
    <>
      {head}
      <div className={styles.toolbar}>
        <UsageFilters />
      </div>
      {list.state === "loading" ? (
        <RowsSkeleton rows={6} table />
      ) : list.state === "error" || list.state === "notFound" ? (
        <Notice
          tone="error"
          action={
            <button type="button" className="tm-btn secondary" onClick={list.reload}>
              {L("ADM_RETRY")}
            </button>
          }
        >
          {L("ADM_UNAVAILABLE_LINE")}
        </Notice>
      ) : list.items.length === 0 ? (
        filtered ? (
          <NoResults query="" things="" onClear={() => set({ type: "", organizationId: "" })} />
        ) : (
          <div className="tm-empty" role="status">
            <strong>{L("ADM_AI_NO_USAGE")}</strong>
            <span>{L("ADM_AI_NO_USAGE_LINE")}</span>
            <button type="button" className="tm-btn secondary" onClick={() => set({ period: "90d" })}>
              {L("ADM_AI_SEE_90D")}
            </button>
          </div>
        )
      ) : (
        <>
          <Counter>{plural(L, locale, list.total, "ADM_AI_N_EXECUTIONS_ONE", "ADM_AI_N_EXECUTIONS")}</Counter>
          <DataTable<ExecutionRow>
            label={L("ADM_AI_EXECUTIONS")}
            rows={list.items}
            rowKey={(row) => row.executionId}
            href={(row) => `/Ai/Uso/Detail/${row.executionId}`}
            columns={[
              {
                key: "execution",
                title: L("ADM_AI_COL_EXECUTION"),
                render: (row) => (
                  <span className={styles.cellMain}>
                    <strong>#{row.executionId}</strong>
                    <span className={styles.cellSub}>{operationName(L, row.operationType)}</span>
                  </span>
                ),
              },
              { key: "org", title: L("ADM_COMPANY"), render: organization },
              {
                key: "status",
                title: L("ADM_AI_COL_STATUS"),
                render: (row) => {
                  const tone = executionTone(L, row.status);
                  return <ServiceState tone={tone.tone}>{tone.text}</ServiceState>;
                },
              },
              { key: "start", title: L("ADM_AI_COL_START"), render: (row) => when(L, locale, row.startedAt) },
              {
                key: "attempts",
                title: L("ADM_AI_COL_ATTEMPTS"),
                numeric: true,
                render: (row) => attemptsText(locale, row.status, row.attemptCount),
              },
              { key: "tokens", title: L("ADM_AI_COL_TOKENS"), numeric: true, render: tokens },
              {
                key: "cost",
                title: L("ADM_AI_COST"),
                numeric: true,
                render: (row) => money(locale, row.totalCost, row.currency),
              },
              {
                key: "time",
                title: L("ADM_AI_COL_TIME"),
                numeric: true,
                render: (row) => duration(L, locale, row.latencyMilliseconds),
              },
            ]}
            mobile={(row) => {
              const tone = executionTone(L, row.status);
              return {
                title: `#${row.executionId}`,
                tag: <ServiceState tone={tone.tone}>{tone.text}</ServiceState>,
                line: [operationName(L, row.operationType), organization(row), when(L, locale, row.startedAt)].join(
                  " · ",
                ),
              };
            }}
          />
          <ListFooter list={list} />
        </>
      )}
    </>
  );
}
